import { Directive, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { DomHandler } from 'primeng/dom';


@Directive({
    selector: '[agprChartTooltip]',
    standalone: false
})
export class ChartTooltipDirective implements OnInit, OnDestroy {

    private tooltip: HTMLDivElement;
    private scrollableParents;
    private tooltipClass = 'agpr-tooltip';

    // Top label string
    @Input() agprChartTooltipLabel: string;
    // Bottom data string
    @Input() tooltipDataText: string;
    // Bottom link string
    @Input() tooltipLinkText: string;
    // Bottom link url
    @Input() tooltipLinkUrl: string;
    // Disable the tooltip
    @Input() tooltipDisabled: boolean = false;

    @Input('initiallyFocusable') initiallyFocusable: boolean = true;

    @Output() linkClicked = new EventEmitter<Event>();

    constructor(private element: ElementRef) {
    }

    @HostListener('mouseenter') onMouseEnter() {
        this.showTooltip();
    }

    @HostListener('mouseleave', ['$event']) onMouseLeave(event) {
        if (event.toElement !== this.element && !event.toElement?.classList?.contains(this.tooltipClass)) {
            this.hideTooltip();
        }
    }

    @HostListener('focus') onMouseFocus() {
        this.showTooltip();
    }

    @HostListener('blur', ['$event']) onMouseBlur(event) {
        if (event.toElement !== this.element && !event.toElement?.classList?.contains(this.tooltipClass)) {
            this.hideTooltip();
        }
    }
    @HostListener('keypress', ['$event']) onEnter(event) {
        if (event.key === 'Enter') {
            this.hideTooltip();
            this.linkClicked.emit(event);
        }

    }

    ngOnInit(): void {
        this.createTooltip();
        this.element.nativeElement.setAttribute('tabIndex', this.initiallyFocusable ? '0' : '-1');
        this.element.nativeElement.setAttribute('aria-label', `${this.agprChartTooltipLabel}, ${this.tooltipDataText}`);
    }

    handleTooltipLinkClick = (event) => {
        this.hideTooltip();
        this.linkClicked.emit(event);
    };

    handleTooltipMouseleave = (event) => {
        if (event.toElement !== this.element) {
            this.hideTooltip();
        }
    };

    handleParentScroll = () => {
        this.hideTooltip();
    };

    createTooltip() {
        const tooltip = document.createElement('div');
        tooltip.classList.add(this.tooltipClass);
        tooltip.style.position = 'absolute';
        tooltip.style.zIndex = '2000';
        tooltip.style.cursor = 'default';

        const tooltipContainer = document.createElement('div');
        tooltipContainer.classList.add(this.tooltipClass);
        tooltipContainer.style.backgroundColor = '#FFFFFF';
        tooltipContainer.style.borderRadius = '7px';
        tooltipContainer.style.border = '1px solid #ced4da';
        tooltipContainer.style.padding = '10px';
        tooltipContainer.style.color = 'black';

        const tooltipLabel = document.createElement('div');
        tooltipLabel.classList.add(this.tooltipClass);
        tooltipLabel.style.fontWeight = '600';
        tooltipLabel.innerHTML = `<p>${this.agprChartTooltipLabel}<p>`;
        tooltipContainer.appendChild(tooltipLabel);

        if (!!this.tooltipDataText || !!this.tooltipLinkText) {
            tooltipLabel.style.borderBottom = '1px solid #ced4da';
            tooltipLabel.style.paddingBottom = '5px';
            const tooltipBottom = document.createElement('div');
            tooltipBottom.classList.add(this.tooltipClass);
            tooltipBottom.style.marginTop = '10px';
            tooltipBottom.style.display = 'flex';
            tooltipBottom.style.alignItems = 'center';

            if (!!this.tooltipDataText) {
                const tooltipData = document.createElement('p');
                tooltipData.classList.add(this.tooltipClass);
                tooltipData.style.marginRight = '20px';
                tooltipData.textContent = this.tooltipDataText;
                tooltipBottom.appendChild(tooltipData);
            }

            if (!!this.tooltipLinkText) {
                const tooltipLink = document.createElement('div');
                tooltipLink.classList.add(this.tooltipClass);

                tooltipLink.innerHTML = `<a
                        id="viewMentionLink"
                        class="${this.tooltipClass} viewMentionLink"
                        href="${this.tooltipLinkUrl}"
                        style="
                            pointer-events: auto;
                            cursor: pointer;
                            text-decoration: underline;
                            color: unset;
                            margin: auto;
                        "
                    >
                        ${this.tooltipLinkText}
                    </a>`;


                tooltipLink.addEventListener('click', this.handleTooltipLinkClick);
                tooltipBottom.appendChild(tooltipLink);
            }

            tooltipContainer.appendChild(tooltipBottom);
        }

        const tooltipArrow = document.createElement('div');
        tooltipArrow.classList.add(this.tooltipClass);
        tooltipArrow.style.height = '0';
        tooltipArrow.style.width = '0';
        tooltipArrow.style.borderTop = '7px solid #ced4da';
        tooltipArrow.style.borderRight = '7px solid transparent';
        tooltipArrow.style.borderLeft = '7px solid transparent';
        tooltipArrow.style.margin = '0 auto';
        tooltipArrow.style.marginTop = '-1px';
        const tooltipArrowInner = document.createElement('div');
        tooltipArrowInner.classList.add(this.tooltipClass);
        tooltipArrowInner.style.width = '0';
        tooltipArrowInner.style.borderTop = '6px solid white';
        tooltipArrowInner.style.borderRight = '6px solid transparent';
        tooltipArrowInner.style.borderLeft = '6px solid transparent';
        tooltipArrowInner.style.margin = '0 auto';
        tooltipArrowInner.style.marginTop = '-8px';

        tooltip.appendChild(tooltipContainer);
        tooltip.appendChild(tooltipArrow);
        tooltip.appendChild(tooltipArrowInner);
        this.tooltip = tooltip;

        this.tooltip.addEventListener('mouseleave', this.handleTooltipMouseleave);
        this.scrollableParents = DomHandler.getScrollableParents(this.element.nativeElement);
        if (this.scrollableParents) {
            this.scrollableParents.forEach(element => {
                element.addEventListener('scroll', this.handleParentScroll);
            });
        }
    }

    showTooltip() {
        if (this.tooltipDisabled) {
            return;
        }
        const target = this.element.nativeElement;
        const cardRect = target.getBoundingClientRect();
        document.body.appendChild(this.tooltip);
        const tooltipRect = this.tooltip.getBoundingClientRect();

        this.tooltip.style.left = cardRect.left + (cardRect.width / 2) - (tooltipRect.width / 2) + 'px';
        this.tooltip.style.top = cardRect.top - (tooltipRect.height - (cardRect.height / 2)) + 'px';
    }

    hideTooltip() {
        this.tooltip.remove();
    }

    ngOnDestroy(): void {
        this.tooltip?.remove();
        if (this.scrollableParents) {
            this.scrollableParents.forEach(element => {
                element.removeEventListener('scroll', this.handleParentScroll);
            });
        }
    }
}

